import type React from 'react';
import { useCallback, useEffect } from 'react';

import { Box, useTheme, styled } from '@mui/material';
import { useMemo, useState } from 'react';
import {
  SelectDropdown,
  Typography,
} from '@forethought-technologies/forethought-elements';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { OrgThemedButton } from 'components/OrgThemedButton';
import { scrollToTheBottom } from 'utils/helpers';
import { selectIsScrollAtTheBottom } from 'reducers/workflows';
import { useAppSelector } from 'hooks';
import type { ProductRecommendationStepComponent } from 'api/types';

interface ProductRecommendationStepProps {
  isLastStep: boolean;
  stepIndex: number;
  widgetComponent: ProductRecommendationStepComponent;
}

const formatPrice = (
  amount: number,
  currencyCode: string,
  locale = navigator.language,
) => {
  return new Intl.NumberFormat(locale, {
    currency: currencyCode,
    style: 'currency',
  }).format(amount);
};

export const ProductRecommendationStep: React.FC<
  React.PropsWithChildren<ProductRecommendationStepProps>
> = ({ widgetComponent }) => {
  const { palette } = useTheme();

  const { isSent } = widgetComponent;
  const { products } = widgetComponent.component_fields;
  const isScrollAtTheBottom = useAppSelector(selectIsScrollAtTheBottom);

  const [selectedProductIndex, setSelectedProductIndex] = useState(0);

  const selectedProduct = useMemo(() => {
    return products[selectedProductIndex];
  }, [selectedProductIndex, products]);

  const [selectedVariantName, setSelectedVariantName] = useState(
    selectedProduct?.variants[0]?.title,
  );

  const variantOptions = useMemo(() => {
    return selectedProduct?.variants.map(variant => {
      return {
        label: variant.title,
        value: variant.title,
      };
    });
  }, [selectedProduct?.variants]);

  const selectedVariant = useMemo(() => {
    return selectedProduct?.variants.find(
      variant => variant.title === selectedVariantName,
    );
  }, [selectedVariantName, selectedProduct?.variants]);

  const handleLoad = useCallback(() => {
    if (isScrollAtTheBottom) {
      scrollToTheBottom('smooth');
    }
  }, [isScrollAtTheBottom]);

  useEffect(() => {
    setSelectedVariantName(selectedProduct?.variants[0]?.title);
  }, [selectedProduct]);

  const openProductDetails = useCallback(() => {
    if (selectedVariant?.details_page_url) {
      window.open(selectedVariant.details_page_url, '_blank');
    }
  }, [selectedVariant?.details_page_url]);

  const hasMultipleProducts = products.length > 1;

  const originalPrice = selectedVariant?.original_price;

  const showDiscount =
    originalPrice &&
    parseFloat(originalPrice) > parseFloat(selectedVariant.price);

  if (!selectedProduct || !selectedVariant) {
    return null;
  }

  const chosenImageURL = selectedVariant.image_url || selectedProduct.image_url;

  return (
    <Container>
      <ClickableTitle onClick={openProductDetails}>
        <Typography variant='font16Bold'>{selectedProduct.title}</Typography>
      </ClickableTitle>
      <CarouselSection hasMultipleProducts={hasMultipleProducts}>
        {hasMultipleProducts && (
          <ArrowButton
            disabled={selectedProductIndex === 0}
            onClick={() => {
              setSelectedProductIndex(prev => prev - 1);
            }}
          >
            <IconChevronLeft
              color={
                selectedProductIndex === 0
                  ? palette.colors.grey[300]
                  : palette.colors.grey[800]
              }
              size={32}
            />
          </ArrowButton>
        )}

        {chosenImageURL && (
          <ClickableImage onClick={openProductDetails}>
            <Thumbnail
              alt={selectedProduct.title}
              onLoad={handleLoad}
              src={chosenImageURL}
            />
          </ClickableImage>
        )}

        {hasMultipleProducts && (
          <ArrowButton
            disabled={selectedProductIndex === products.length - 1}
            onClick={() => {
              setSelectedProductIndex(prev => prev + 1);
            }}
          >
            <IconChevronRight
              color={
                selectedProductIndex === products.length - 1
                  ? palette.colors.grey[300]
                  : palette.colors.grey[800]
              }
              size={32}
            />
          </ArrowButton>
        )}
      </CarouselSection>
      <PriceSection>
        <Typography variant='font18Bold'>
          {formatPrice(
            parseFloat(selectedVariant.price),
            selectedVariant.currency_code,
          )}
        </Typography>
        {showDiscount && (
          <>
            <Typography color={palette.colors.grey[300]} variant='font14Bold'>
              <s>
                {formatPrice(
                  parseFloat(originalPrice),
                  selectedVariant.currency_code,
                )}
              </s>
            </Typography>

            <Typography color='#F88D00' variant='font14Bold'>
              -
              {formatPrice(
                parseFloat(originalPrice) - parseFloat(selectedVariant.price),
                selectedVariant.currency_code,
              )}
            </Typography>
          </>
        )}
      </PriceSection>
      <Typography variant='font14'>{selectedProduct.description}</Typography>

      <FooterSection>
        {selectedProduct.variants.length > 1 && (
          <Box
            sx={{
              '.MuiBox-root': {
                fontSize: '14px',
              },
              '.MuiInputBase-root': {
                borderRadius: '24px',
                height: '40px',
                maxWidth: '150px',
              },
            }}
          >
            <SelectDropdown
              id='variant-select'
              onChange={e => {
                setSelectedVariantName(e.target.value);
              }}
              options={variantOptions || []}
              value={selectedVariantName || ''}
            />
          </Box>
        )}
        <OrgThemedButton
          disabled={isSent}
          onClick={() => {
            if (!selectedVariant.is_add_to_cart_enabled) {
              openProductDetails();
            }
          }}
          size='large'
          sx={{
            '&:hover': {
              background: '#d7d957',
            },
            background: '#f2f551',
            color: 'black',
            width: '100%',
          }}
          variant='contained'
        >
          {selectedVariant.is_add_to_cart_enabled
            ? 'Add to cart'
            : 'See details'}
        </OrgThemedButton>
      </FooterSection>
    </Container>
  );
};

const ArrowButton = styled('button')<{ disabled: boolean }>`
  border: none;
  height: 40px;
  width: 40px;
  background: transparent;
  cursor: ${props => (props.disabled ? 'default' : 'pointer')};
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;

  &:hover {
    background-color: ${props =>
      props.disabled ? 'transparent' : props.theme.palette.colors.slate[200]};
  }
`;

const ClickableTitle = styled('button')`
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  text-align: left;

  &:hover {
    text-decoration: underline;
  }

  &:focus {
    outline: none;
  }
`;

const ClickableImage = styled('button')`
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  transition: opacity 0.2s ease;
  border-radius: 8px;

  &:hover {
    opacity: 0.95;
  }

  &:focus {
    outline: none;
  }
`;

const Thumbnail = styled('img')`
  height: 200px;
  width: auto;
`;

const Container = styled('div')`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 8px;
  gap: 12px;
  margin: 4px 24px 24px 24px;
  background: #fff;
  box-shadow: 1px 0px 8px 0px rgba(0, 0, 0, 0.08);
  border: 1px solid #f4f4f4;
`;

const CarouselSection = styled('div')<{ hasMultipleProducts: boolean }>`
  display: flex;
  gap: 24px;
  justify-content: ${props =>
    props.hasMultipleProducts ? 'space-between' : 'center'};
  align-items: center;
  margin-top: 24px;
  margin-bottom: 24px;
`;

const PriceSection = styled('div')`
  display: flex;
  gap: 8px;
`;

const FooterSection = styled('div')`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;
